import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Kaharian v3.2 Studio health hook.
 *
 * Probes /api/creative-studio/health/{category} on mount + on tab-visibility change.
 * Handles countdown tick and auto-retry armed by `onProvidersUnavailable()`.
 *
 * Usage:
 *   const { health, cooldown, onProvidersUnavailable, armAutoRetry } =
 *     useStudioHealth('VIDEO', { onRecover: () => generateInternal() });
 *
 *   // In your error catch:
 *   if (isProvidersDown) { onProvidersUnavailable(detail, { armRetry: true }); }
 */
export function useStudioHealth(category, { onRecover } = {}) {
  const [health, setHealth] = useState({
    available: true,
    message: '',
    retry_after_seconds: 0,
    checked_at: 0,
  });
  const [cooldown, setCooldown] = useState(0);
  const pendingRetryRef = useRef(false);
  const onRecoverRef = useRef(onRecover);
  useEffect(() => { onRecoverRef.current = onRecover; }, [onRecover]);

  // Probe on mount + on tab-visibility change
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const { data } = await axios.get(`${API}/api/creative-studio/health/${category}`);
        if (cancelled) return;
        setHealth(data);
        if (!data.available && data.retry_after_seconds > 0) setCooldown(data.retry_after_seconds);
      } catch { /* fail open */ }
    };
    probe();
    const visHandler = () => { if (!document.hidden) probe(); };
    document.addEventListener('visibilitychange', visHandler);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', visHandler); };
  }, [category]);

  // Countdown tick
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // When cooldown hits 0 and we're still marked unavailable, re-probe
  useEffect(() => {
    if (cooldown !== 0 || health.available) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/creative-studio/health/${category}`);
        setHealth(data);
        if (data.available && pendingRetryRef.current) {
          pendingRetryRef.current = false;
          setTimeout(() => onRecoverRef.current && onRecoverRef.current(), 400);
        } else if (!data.available) {
          setCooldown(data.retry_after_seconds || 30);
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooldown, health.available, category]);

  const onProvidersUnavailable = useCallback((detail, { armRetry } = {}) => {
    setHealth({
      available: false,
      message: detail?.message || '',
      retry_after_seconds: 30,
      checked_at: Date.now() / 1000,
    });
    setCooldown(30);
    if (armRetry) pendingRetryRef.current = true;
  }, []);

  return { health, cooldown, onProvidersUnavailable, pendingRetryRef };
}

/**
 * Tiny banner component used at the top of every Studio when providers are down.
 * Props: { category, health, cooldown, pendingRetry }
 */
export function StudioHealthBanner({ category, health, cooldown, pendingRetry, className = '' }) {
  if (health.available) return null;
  const labelMap = {
    VIDEO: 'Video generation',
    IMAGE: 'Image generation',
    MUSIC: 'Music generation',
    AVATAR: 'Avatar training',
    TALKING_VIDEO: 'Avatar video',
    LONG_VIDEO: 'Long video',
  };
  const label = labelMap[category] || 'This feature';
  return (
    <div
      className={`bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3 ${className}`}
      data-testid={`${category.toLowerCase()}-providers-unavailable-banner`}
    >
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5 19a2 2 0 01-1.73-3L10.27 4a2 2 0 013.46 0l7 12A2 2 0 0119 19H5z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">{label} temporarily unavailable</p>
        <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
          {health.message || 'Upstream providers are in maintenance. No credits are being charged.'}
        </p>
        {cooldown > 0 && (
          <p className="text-[11px] text-amber-600 mt-1.5" data-testid={`${category.toLowerCase()}-retry-countdown`}>
            {pendingRetry ? `Auto-retrying in ${cooldown}s…` : `Rechecking in ${cooldown}s…`}
          </p>
        )}
      </div>
    </div>
  );
}
